/* eslint-disable sonarjs/cognitive-complexity */
import { G } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { startOfToday } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  RaRecord,
  required,
  SelectInput,
  TextInput,
  useGetIdentity,
  useGetList,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { UseFormReturn, useFormContext, useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';

import { EMPLOYEE_SAVINGS_RETIREMENT_AGE } from '@Common/constants/employee_savings';
import { GetB2bCompaniesIdEndpoint } from '@Common/types/bo/b2b/companies';
import { B2bContract } from '@Common/types/bo/b2b/contracts';
import { GetB2bEmployeesOneResponse } from '@Common/types/bo/b2b/employees';
import { EmployeeStatus } from '@Common/types/bo/b2b/employees_properties';
import { GetB2bCompaniesPlansResponse } from '@Common/types/bo/b2b/plans';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';

import { IdArrayInput, ShowSection } from '@sh4res/bo-common/components';
import {
  leavingReasonChoices,
  socialStatusChoices,
  statusChoices,
} from '@sh4res/bo-common/components/B2bEmployee/choices';
import { getDefaultRetirementDate } from '@sh4res/bo-common/components/B2bEmployee/common';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useResourceBasePath, useValueChanged } from '@sh4res/bo-common/hooks';
import { inputDisabledProps } from '@sh4res/bo-common/utils';
import { isBoExternalIdentity } from '@sh4res/bo-common/utils/b2b_auth_role';
import { tryToLocalDate } from '@sh4res/bo-common/utils/date';
import { isBoExternal } from '@sh4res/bo-common/utils/env';
import {
  erAffiliationRequiresTaigaIdValidation,
  getWorkEndingDateValidator,
  workStartingDate,
} from '@sh4res/bo-common/utils/validators/b2b';

type Company = GetB2bCompaniesIdEndpoint['Client']['ResponseBody'];

interface ContractFromApiForStep extends RaRecord {
  contract_number: string;
}

interface GetB2bContractsForEmployeeStepQuery extends BoListQueryBase {
  filter?: {
    b2b_company_id?: number;
    is_active?: boolean;
    id?: number;
    query?: string;
  };
}

type EmploymentInformationStepProps = {
  disableRetirementDateEdit?: boolean;
  companyId?: number;
  testId?: string;
};

const editableEmploymentStatuses: EmployeeStatus[] = ['current', 'past', 'retired'];

export const setStatus = (
  leavingReason: string,
  workEndingDate: string | null,
  formContext: UseFormReturn,
) => {
  let newStatus: EmployeeStatus;

  const workEndingDateValue = tryToLocalDate(workEndingDate);
  if (G.isNullable(workEndingDateValue)) {
    return;
  }

  if (workEndingDateValue >= startOfToday()) {
    newStatus = 'current';
  } else {
    newStatus = match<string, EmployeeStatus>(leavingReason)
      .with('retirement', () => 'retired')
      .with('', () => 'current')
      .otherwise(() => 'past');
  }

  formContext.setValue('status', newStatus);
};

export const EmploymentInformationStep = ({
  disableRetirementDateEdit,
  companyId: companyIdFromProps,
  testId,
}: EmploymentInformationStepProps) => {
  const record = useRecordContext<GetB2bEmployeesOneResponse>();
  const { data: identity } = useGetIdentity();
  const formContext = useFormContext();
  const dob = formContext.getValues('date_of_birth');
  const companyId = formContext.getValues('company_id') ?? companyIdFromProps;
  const leavingReasonValue = formContext.getValues('leaving_reason');
  const workEndingDateValue = formContext.getValues('work_ending_date');
  const statusValue = formContext.getValues('status');
  const watchedContractIds = useWatch({ name: 'contract_ids' });
  const contractIds = useMemo((): number[] => {
    if (!Array.isArray(watchedContractIds)) {
      return [];
    }

    return watchedContractIds.filter((id): id is number => typeof id === 'number');
  }, [watchedContractIds]);
  const taigaPolicyHolderId = useWatch({ name: 'id_external_taiga' });
  const { trigger, unregister } = formContext;

  const resourceBasePath = useResourceBasePath();

  const { data: contracts } = useGetList<B2bContract>(
    `${resourceBasePath}contracts`,
    {
      filter: { id: contractIds },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: Array.isArray(watchedContractIds) },
  );

  useValueChanged(useWatch({ name: 'work_starting_date' }), () => {
    formContext.trigger('work_ending_date');
  });

  const changedFromInactiveToCurrent =
    G.isNotNullable(record) &&
    editableEmploymentStatuses.includes(record.status) &&
    record.status !== 'current' &&
    statusValue === 'current';

  const contractSearchFilter = useMemo(
    () => (companyId ? { b2b_company_id: companyId, is_active: true } : { id: -1 }),
    [companyId],
  );

  const handleLeavingReasonChange = useCallback(
    (newValue: string) => {
      setStatus(newValue, workEndingDateValue, formContext);
      if (changedFromInactiveToCurrent) {
        formContext.clearErrors('work_ending_date');
      }
    },
    [formContext, workEndingDateValue, changedFromInactiveToCurrent],
  );

  const handleWorkEndingDateChange = useCallback(
    (newValue: string) => {
      if (!changedFromInactiveToCurrent) {
        setStatus(leavingReasonValue, newValue, formContext);
      }
    },
    [formContext, leavingReasonValue, changedFromInactiveToCurrent],
  );

  const handleStatusChange = useCallback(
    (newValue: string) => {
      if (
        newValue === 'current' &&
        G.isNotNullable(record) &&
        editableEmploymentStatuses.includes(record.status) &&
        record.status !== 'current'
      ) {
        formContext.clearErrors(['work_ending_date', 'leaving_reason']);

        formContext.setValue('work_ending_date', null, {
          shouldDirty: true,
          shouldValidate: true,
        });

        formContext.setValue('leaving_reason', null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [formContext, record],
  );

  const handleCSPCodeChange = useCallback(
    (value: string) => {
      const code = value.replace(/ /g, '');
      formContext.setValue('csp_code', code.length === 0 ? null : code, {
        shouldValidate: true,
      });
    },
    [formContext],
  );
  const isRecordStatusFinal =
    !G.isUndefined(record) &&
    !editableEmploymentStatuses.includes(record.status) &&
    !changedFromInactiveToCurrent;

  const { data: fetchedCompany } = useGetOne<Company>(
    `${resourceBasePath}companies`,
    {
      id: companyId,
    },
    { enabled: G.isNotNullable(companyId) && !isBoExternal() },
  );

  const externalIdentity = isBoExternalIdentity(identity) ? identity : undefined;
  const company = isBoExternal()
    ? externalIdentity?.companies.find((company) => company.id === companyId)
    : fetchedCompany;

  useEffect(() => {
    if (G.isNotNullable(company) && !isRecordStatusFinal && !record?.retirement_date) {
      formContext.setValue(
        'retirement_date',
        getDefaultRetirementDate(dob, company.retirement_age ?? EMPLOYEE_SAVINGS_RETIREMENT_AGE),
      );
    }
  }, [company, dob, isRecordStatusFinal, record, formContext]);

  const { data: plans } = useGetList<GetB2bCompaniesPlansResponse[number]>(
    `${resourceBasePath}companies/plans`,
    {
      filter: {
        b2b_contract_id: contractIds,
      },
      sort: { field: 'created_at', order: 'DESC' },
    },
    { enabled: contractIds.length > 0 },
  );

  const affiliationsFieldMounted =
    G.isNotNullable(company) && (contractIds.length === 0 || G.isNotNullable(plans));

  const contractIdsKey = contractIds.join(',');
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  useEffect(() => {
    if (!affiliationsFieldMounted || G.isUndefined(record?.id)) {
      return;
    }
    triggerRef.current('contract_ids');
  }, [affiliationsFieldMounted, contractIdsKey, contracts, taigaPolicyHolderId, record?.id]);

  const unregisterRef = useRef(unregister);
  unregisterRef.current = unregister;

  useEffect(() => {
    if (G.isUndefined(record?.id)) {
      return;
    }

    return () => {
      // Drop the rules when leaving this step so a hidden affiliation field
      // cannot block Next on an earlier step. The selected ids stay in the form.
      unregisterRef.current('contract_ids', { keepValue: true });
    };
  }, [record?.id]);

  if (!affiliationsFieldMounted) {
    return;
  }

  const isCSPCodeRequired = plans?.some((plan) =>
    plan.matching_contribution_config?.type.includes('csp_code'),
  );

  let cspCodesChoices: { id: string | null; name: string }[] = [];
  if (isCSPCodeRequired && G.isNotNullable(plans)) {
    const uniqueCSPCodes = plans.reduce((codes: string[], plan) => {
      if (plan.matching_contribution_config?.type === 'csp_code') {
        const codesToAdd = Object.keys(plan.matching_contribution_config.limits_per_csp_code);

        return [...new Set(codes.concat(codesToAdd))];
      }

      if (plan.matching_contribution_config?.type === 'csp_code_per_cash_source') {
        const codesToAdd = plan.matching_contribution_config.supported_contribution_types.reduce(
          (codesForAllCashSource: string[], cashSourceConfig) => {
            const codesToAddForCashSource = Object.keys(cashSourceConfig.limits_per_csp_code);

            return [...new Set(codesForAllCashSource.concat(codesToAddForCashSource))];
          },
          [],
        );

        return [...new Set(codes.concat(codesToAdd))];
      }

      if (plan.matching_contribution_config?.type === 'csp_code_per_cash_source_brackets') {
        const codesToAdd = plan.matching_contribution_config.supported_contribution_types.reduce(
          (codesForAllCashSource: string[], cashSourceConfig) => {
            const codesToAddForCashSource = Object.keys(cashSourceConfig.brackets_per_csp_code);

            return [...new Set(codesForAllCashSource.concat(codesToAddForCashSource))];
          },
          [],
        );

        return [...new Set(codes.concat(codesToAdd))];
      }

      return codes;
    }, []);

    cspCodesChoices = uniqueCSPCodes
      .filter((code) => code !== '')
      .map((code) => ({
        id: code,
        name: code,
      }));
  }

  const defaultRetirementDate = getDefaultRetirementDate(
    dob,
    company.retirement_age ?? EMPLOYEE_SAVINGS_RETIREMENT_AGE,
  );

  return (
    <Box>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.employees.labels.work_info"
        noCard={true}>
        <DateInput
          source="work_starting_date"
          validate={isRecordStatusFinal ? [] : [required(), workStartingDate()]}
          {...inputDisabledProps(isRecordStatusFinal)}
          isRequired
          data-testid={testId ? `${testId}_WorkStartingDateInput` : undefined}
        />
        <SelectInput
          source="status"
          validate={isRecordStatusFinal ? [] : [required()]}
          isRequired
          choices={statusChoices}
          onChange={(evt) => handleStatusChange(evt.target.value)}
          {...inputDisabledProps(
            G.isNotNullable(record) && !editableEmploymentStatuses.includes(record.status),
          )}
          data-testid={testId ? `${testId}_StatusSelect` : undefined}
        />
        <SelectInput
          source="leaving_reason"
          choices={leavingReasonChoices.filter((leavingReason) => leavingReason.id !== 'decease')}
          defaultValue={null}
          validate={!isRecordStatusFinal && statusValue !== 'current' ? [required()] : []}
          isRequired={statusValue !== 'current' && !changedFromInactiveToCurrent}
          onChange={(evt) => handleLeavingReasonChange(evt.target.value)}
          {...inputDisabledProps(isRecordStatusFinal)}
          data-testid={testId ? `${testId}_LeavingReasonSelect` : undefined}
        />
        <DateInput
          source="work_ending_date"
          defaultValue={null}
          validate={getWorkEndingDateValidator(
            statusValue,
            leavingReasonValue,
            changedFromInactiveToCurrent,
          )}
          isRequired={statusValue !== 'current'}
          onChange={(evt) => handleWorkEndingDateChange(evt.target.value)}
          {...inputDisabledProps(isRecordStatusFinal)}
          data-testid={testId ? `${testId}_WorkEndingDateInput` : undefined}
        />
        <TextInput
          source="role_in_company"
          defaultValue={null}
          {...inputDisabledProps(isRecordStatusFinal)}
          data-testid={testId ? `${testId}_RoleInCompanyInput` : undefined}
        />
        {!isCSPCodeRequired && (
          <TextInput
            source="csp_code"
            defaultValue={null}
            onChange={(event) => handleCSPCodeChange(event.target.value)}
            data-testid={testId ? `${testId}_CspCodeInput` : undefined}
          />
        )}
        {isCSPCodeRequired && (
          <SelectInput
            source="csp_code"
            defaultValue={null}
            choices={cspCodesChoices}
            data-testid={testId ? `${testId}_CspCodeSelect` : undefined}
          />
        )}
        {/* Hide this input if the employee is being created because it's already in the personal information step */}
        {!G.isUndefined(record?.id) && (
          <IdArrayInput<GetB2bContractsForEmployeeStepQuery, ContractFromApiForStep>
            data-testid={testId ? `${testId}_ContractIdsInput` : undefined}
            source="contract_ids"
            reference={`${resourceBasePath}contracts`}
            addIdLabel="resources.bo_common.b2b.employees.fields.contract_ids"
            getLabel={(contract: ContractFromApiForStep) =>
              contract.contract_number || `ID: ${contract.id}`
            }
            searchLimit={50}
            searchFilter={contractSearchFilter}
            searchTarget="query"
            disabled={!companyId}
            fullWidth
            isRequired
            validate={[required(), erAffiliationRequiresTaigaIdValidation(contracts)]}
            fetchChoicesOnMountIfFilterPresent={true}
            displayPrefetchedOptionsWithoutSearchText={true}
          />
        )}

        <SelectInput
          source="social_status"
          validate={[required()]}
          isRequired
          defaultValue={'TS'}
          choices={socialStatusChoices}
          data-testid={testId ? `${testId}_SocialStatusSelect` : undefined}
        />
        <DateInput
          source="retirement_date"
          defaultValue={defaultRetirementDate}
          validate={[required()]}
          onBlur={(event) => {
            if (event.target.value === '') {
              formContext.setValue('retirement_date', defaultRetirementDate);
            }
          }}
          {...inputDisabledProps(disableRetirementDateEdit ?? false)}
          data-testid={testId ? `${testId}_RetirementDateInput` : undefined}
        />
      </ShowSection>
    </Box>
  );
};
